/* eslint-disable prettier/prettier */
import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { addMinutes, parseISO, format } from 'date-fns';

import { useToast } from '../../hooks/toast';

import api from '../../services/api';

import SelectSync, { IOption } from '../SelectSync';
import Input from '../Input';
import Loading from '../Loading';

import {
  CONFIRMACAO_AGENDA,
  optionsHorasAtendimento,
  STATUS_AGENDA,
} from '../../utils/funcoes';
import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import { IPedidoExames } from '../../pages/PedidoExamesBusca';

import SelectAsyncCadastro from '../SelectAsyncCadastro';
import MedicoCadastro from '../../pages/Medico/MedicoCadastro';
import UnidadeSaudeCadastro from '../../pages/UnidadeSaude/UnidadeSaudeCadastro';
import { useAuth } from '../../hooks/auth';
import { IPedidoEspecialidade } from '../../pages/PacienteAtendimento/PedidoEncaminhamento';

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
}

interface IFormData {
  AgeConfCodigo: number;
  AgeData: Date;
  AgeHora: string;
  AgeMedico: string;
  AgeUnidadeSaude: string;
}

interface IReturnAgendamento {
  AgeCodigo: number;
  AgeConfCodigo: number;
}

type ComponentProps = {
  PedCodigo: number;
  tipoExame?: boolean;
  handleFechar(dados: IReturnAgendamento): void;
};

const PedidoExame: React.FC<ComponentProps> = ({
  PedCodigo,
  tipoExame = true,
  handleFechar,
}) => {
  const formRef = useRef<FormHandles>(null);

  const { clinica } = useAuth();

  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pedidoExame, setPedidoExame] = useState<IPedidoExames>();
  const [
    pedidoEspecialidade,
    setPedidoEspecialidade,
  ] = useState<IPedidoEspecialidade>();
  const [procedimentos, setProcedimentos] = useState<IProcedimento[]>();
  const [optionsAgendas, setOptionsAgendas] = useState<IOption[]>([]);
  const [finalizado, setFinalizado] = useState(false);
  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };

  const [medico, setMedico] = useState({ codigo: 0, nome: '' });
  const [showModalMedico, setShowModalMedico] = useState(false);
  const [unidadeSaude, setUnidadeSaude] = useState({ codigo: 0, nome: '' });
  const [showModalUnidadeSaude, setShowModalUnidadeSaude] = useState(false);

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        if (tipoExame) {
          const response = await api.get<IPedidoExames>(
            `pedidoexames/${PedCodigo}?limite=1000`,
          );
          if (response.data.PedFinalizado) {
            setFinalizado(true);
          } else {
            const responseAgendas = await api.get<IOption[]>(
              `agendas/porperfil?ConPerCodigo=${response.data.PedPerCodigo}&ConClinica=${response.data.PedCliCodigo}`,
            );

            setOptionsAgendas(responseAgendas.data);

            const responseProcedimentos = await api.get<IProcedimento[]>(
              `procedimentos/porcodigos?codigos=${response.data.itens
                .map(ite => ite.ItmProCodigo)
                .join()}`,
            );
            setProcedimentos(responseProcedimentos.data);
          }
          setPedidoExame(response.data);
        }
        if (!tipoExame) {
          const response = await api.get<IPedidoEspecialidade>(
            `pedidoEspecialidade/${PedCodigo}?limite=1000`,
          );
          if (response.data.PedFinalizado) {
            setFinalizado(true);
          } else {
            const responseAgendas = await api.get<IOption[]>(
              `agendas/porperfil?ConPerCodigo=${response.data.PedPerCodigo}&ConClinica=${response.data.PedCliCodigo}`,
            );

            setOptionsAgendas(responseAgendas.data);

            const responseProcedimentos = await api.get<IProcedimento>(
              `procedimentos/consultaPorPerfil/${response.data.PedPerCodigo}`,
            );
            setProcedimentos([responseProcedimentos.data]);
          }
          setPedidoEspecialidade(response.data);
        }
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (PedCodigo) loadDados();
  }, [PedCodigo, addToast, tipoExame]);

  const dadosMedico = useCallback(
    (ageMedico: string): number | undefined => {
      if (ageMedico !== '') return Number(ageMedico);
      if (medico.codigo !== 0) return medico.codigo;
      return undefined;
    },
    [medico.codigo],
  );

  const dadosUnidadeSaude = useCallback(
    (ageUnidadeSaude: string): number | undefined => {
      if (ageUnidadeSaude !== '') return Number(ageUnidadeSaude);
      if (unidadeSaude.codigo !== 0) return unidadeSaude.codigo;
      return undefined;
    },
    [unidadeSaude.codigo],
  );

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      setLoading(true);
      try {
        if (tipoExame && !pedidoExame)
          throw new Error('Pedido de Exame não encontrado');
        if (tipoExame === false && !pedidoEspecialidade)
          throw new Error('Pedido de Encaminhamento não encontrado');
        if (!procedimentos) throw new Error('Procedimentos não encontrados');

        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeConfCodigo: Yup.number().required(),
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
          ...(clinica.CliIdentificaPedidoExame
            ? {
              AgeMedico: Yup.string().required(
                'Informe o Médico que indicou o exame',
              ),
              AgeUnidadeSaude: Yup.string().required(
                'Informe o Unidade de Saúde que indicou o exame',
              ),
            }
            : {}),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const pedCodigo =
          pedidoExame?.PedCodigo || pedidoEspecialidade?.PedCodigo;
        const PacCodigo =
          pedidoExame?.PedPacCodigo || pedidoEspecialidade?.PedPacCodigo;
        const CliCodigo =
          pedidoExame?.PedCliCodigo || pedidoEspecialidade?.PedCliCodigo;

        const agendamento = {
          AgeConfCodigo: dados.AgeConfCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, 5),
          AgePacCodigo: PacCodigo,
          AgeFormaPag: 9,
          AgeClinica: CliCodigo,
          AgeObs: '',
          AgeStatus: STATUS_AGENDA.AGENDADO,
          AgeConfirmado: CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO,
          AgePlano: false,
          duracao: 5,
          procedimentos: procedimentos.map(pro => {
            return {
              ProCodigo: pro.ProCodigo,
              ProNome: pro.ProNome,
              ProValor: pro.ProValor,
            };
          }),
          PedCodigo: pedCodigo,
          PedExame: tipoExame,
          AgeMedico: dadosMedico(dados.AgeMedico),
          AgeUnidadeSaude: dadosUnidadeSaude(dados.AgeUnidadeSaude),
        };

        const appointment = await api.post<IReturnAgendamento>(
          '/agendamentos',
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda
        addToast({ title: 'Agendamento salvo', type: 'success' });
        handleFechar({
          AgeCodigo: appointment.data.AgeCodigo,
          AgeConfCodigo: appointment.data.AgeConfCodigo,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      tipoExame,
      pedidoExame,
      pedidoEspecialidade,
      procedimentos,
      clinica.CliIdentificaPedidoExame,
      dadosMedico,
      dadosUnidadeSaude,
      addToast,
      handleFechar,
    ],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => { }, []);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      {finalizado ? (
        <h5 className="text-center">Status: Pedido de Exame Finalizado</h5>
      ) : (
        <>
          <h5>Gerar agendamento</h5>
          <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
            <Row className="mb-2">
              <Col md={4} sm={6}>
                <SelectSync
                  name="AgeConfCodigo"
                  label="Selecione a Agenda"
                  // isClearable={false}
                  placeholder="Selecione"
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsAgendas}
                />
              </Col>
              <Col md={3} sm={6}>
                <Input name="AgeData" label="Data" type="date" />
              </Col>
              <Col md={3} sm={6}>
                <SelectSync
                  name="AgeHora"
                  label="Hora"
                  placeholder="Selecione"
                  isClearable={false}
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsHora}
                />
              </Col>
              <Col md={2} sm={6}>
                <br />
                <Button
                  type="submit"
                  size="sm"
                  className="mt-1"
                  disabled={loading}
                >
                  Gerar
                </Button>
              </Col>
            </Row>
            <Row className="mb-2" noGutters>
              <Col
                xs={6}
                md={5}
                sm={6}
                className={`${!clinica.CliIdentificaPedidoExame ? 'd-none' : ''
                  }`}
              >
                <SelectAsyncCadastro
                  urlApi="medico/combo?termoBusca="
                  name="AgeMedico"
                  isDisabled={false}
                  valorSelecionado={medico}
                  labelSelect="Medico"
                  showModal={showModalMedico}
                  setShowModal={setShowModalMedico}
                  limparValor={() => setMedico({ codigo: 0, nome: '' })}
                >
                  <MedicoCadastro
                    medico={undefined}
                    setShowModal={setShowModalMedico}
                  />
                </SelectAsyncCadastro>
              </Col>
              <Col
                xs={6}
                md={5}
                sm={6}
                className={`px-1 ${!clinica.CliIdentificaPedidoExame ? 'd-none' : ''
                  }`}
              >
                <SelectAsyncCadastro
                  urlApi="unidadeSaude/combo?termoBusca="
                  name="AgeUnidadeSaude"
                  isDisabled={false}
                  valorSelecionado={unidadeSaude}
                  labelSelect="Unidade de Saúde"
                  showModal={showModalUnidadeSaude}
                  setShowModal={setShowModalUnidadeSaude}
                  limparValor={() => setUnidadeSaude({ codigo: 0, nome: '' })}
                >
                  <UnidadeSaudeCadastro
                    unidadeSaude={undefined}
                    setShowModal={setShowModalUnidadeSaude}
                  />
                </SelectAsyncCadastro>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Container>
  );
};

export default PedidoExame;
